import { useState, useRef, useCallback, useEffect, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'

import { callMachine } from '../telephony/callMachine'
import { useCallPhase } from '../telephony/selectors'

/**
 * « TIRER POUR ACTUALISER » DU CHÂSSIS (#260).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * CE QUI A CHANGÉ, ET POURQUOI
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Monté sans `onRefresh` autour de TOUTES les pages authentifiées
 * (`AppShell`), le composant rechargeait la page entière : un agent qui
 * remontait en haut d'une liste d'un geste rechargeait l'application —
 * filtres, brouillons et notes perdus, et EN PLEIN APPEL le rechargement
 * détruisait le `Device` Twilio et coupait la communication.
 *
 * Désormais :
 *   1. le geste ne recharge JAMAIS la page. Par défaut il invalide le cache
 *      TanStack Query (`invalidateQueries()` : les requêtes actives repartent,
 *      l'écran reste en place) puis publie `PULL_REFRESH_EVENT` sur `window`
 *      pour les écrans dont les données ne passent pas par React Query (la
 *      page Contacts s'y abonne) ;
 *   2. il est DÉSARMÉ hors repos : même prédicat que la mise à jour du service
 *      worker (`pwa/swUpdate.ts`) — `callMachine.getSnapshot().phase ===
 *      'idle'`, lu via le sélecteur existant `useCallPhase` (aucune écriture
 *      sous `telephony/`). `preview` (enchaînement armé), `wrapup`
 *      (qualification en saisie) et toute ligne vivante n'actualisent rien ;
 *   3. il ne s'arme que si le conteneur est en haut ET que l'appui ne commence
 *      pas dans un panneau intérieur déjà défilé (script, notes, liste
 *      interne) : sans cette seconde garde, `main.scrollTop` reste à 0 et tout
 *      glissement vers le bas dans le panneau armait le geste ;
 *   4. seuil : 80 px de tirage affiché (160 px de doigt, résistance 0,5).
 *
 * Le rechargement complet ne doit pas revenir dans ce fichier :
 * `PullToRefresh.spec.tsx` le garde sur le source.
 */

interface PullToRefreshProps {
  children: ReactNode
  /** Actualisation propre à l'écran. Absente : invalidation du cache + événement. */
  onRefresh?: () => Promise<void> | void
}

/** Distance de tirage (après résistance) qui déclenche l'actualisation. */
export const PULL_THRESHOLD = 80
/** Tirage maximal affiché. */
const MAX_PULL = 120
/** Résistance : le contenu suit le doigt à moitié. */
const RESISTANCE = 0.5

/**
 * Événement publié sur `window` après l'invalidation par défaut : les écrans
 * qui chargent hors React Query (hooks maison) s'y abonnent pour se recharger.
 */
export const PULL_REFRESH_EVENT = 'app:pull-refresh'

/** Prédicat de repos — le même que la mise à jour du service worker. */
export function isPullRefreshAllowed(): boolean {
  return callMachine.getSnapshot().phase === 'idle'
}

/**
 * Vrai si l'appui commence dans un élément intérieur (entre la cible et le
 * conteneur) qui est déjà défilé : le geste « remonter » appartient alors à
 * ce panneau, pas à l'actualisation.
 */
function startsInScrolledPanel(target: EventTarget | null, container: HTMLElement): boolean {
  let node = target instanceof Element ? target : null
  while (node && node !== container) {
    if (node.scrollTop > 0) return true
    node = node.parentElement
  }
  return false
}

export function PullToRefresh({ children, onRefresh }: PullToRefreshProps) {
  const { t } = useTranslation('shell')
  const queryClient = useQueryClient()
  const armed = useCallPhase() === 'idle'

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  // Refs : les gestionnaires tactiles lisent l'état COURANT du geste, sans
  // dépendre d'un re-rendu entre `touchstart` et `touchmove`.
  const startY = useRef(0)
  const pulling = useRef(false)
  const distance = useRef(0)
  const refreshing = useRef(false)

  const reset = useCallback(() => {
    pulling.current = false
    distance.current = 0
    setPullDistance(0)
  }, [])

  // Un appel qui démarre pendant un tirage le désarme sur-le-champ.
  useEffect(() => {
    if (!armed) reset()
  }, [armed, reset])

  // Find the scrollable parent element
  const getScrollTop = useCallback(() => {
    // Check for overflow container (main element in AppShell)
    const scrollParent = containerRef.current?.closest('main')
    if (scrollParent) {
      return scrollParent.scrollTop
    }
    // Fallback to window scroll
    return window.scrollY || document.documentElement.scrollTop
  }, [])

  const handleRefresh = useCallback(async () => {
    // Seconde garde, au moment du déclenchement : la phase a pu changer
    // entre l'appui et le relâcher.
    if (!isPullRefreshAllowed()) return
    refreshing.current = true
    setIsRefreshing(true)
    try {
      if (onRefresh) {
        await onRefresh()
      } else {
        await queryClient.invalidateQueries()
        window.dispatchEvent(new Event(PULL_REFRESH_EVENT))
      }
    } catch (error) {
      console.error('Refresh failed:', error)
    } finally {
      refreshing.current = false
      setIsRefreshing(false)
    }
  }, [onRefresh, queryClient])

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      const container = containerRef.current
      if (!armed || refreshing.current || !container) return
      // Only enable pull-to-refresh when at the top of the scrollable area
      if (getScrollTop() > 0 || startsInScrolledPanel(e.target, container)) return
      startY.current = e.touches[0].clientY
      pulling.current = true
    },
    [armed, getScrollTop],
  )

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!pulling.current || refreshing.current) return
      if (!isPullRefreshAllowed() || getScrollTop() > 0) {
        reset()
        return
      }
      const diff = e.touches[0].clientY - startY.current
      if (diff > 0) {
        // Prevent default scroll behavior when pulling down
        e.preventDefault()
        const next = Math.min(diff * RESISTANCE, MAX_PULL)
        distance.current = next
        setPullDistance(next)
      }
    },
    [getScrollTop, reset],
  )

  const handleTouchEnd = useCallback(() => {
    if (!pulling.current) return
    const reached = distance.current >= PULL_THRESHOLD
    reset()
    if (reached && !refreshing.current) void handleRefresh()
  }, [handleRefresh, reset])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Use passive: false for touchmove to allow preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true })

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      container.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  // Pendant l'actualisation, l'indicateur reste visible (à mi-course) : c'est
  // le seul retour visuel quand l'écran ne bouge pas.
  const shownDistance = isRefreshing ? PULL_THRESHOLD * 0.6 : pullDistance
  const progress = Math.min(shownDistance / PULL_THRESHOLD, 1)
  const rotation = progress * 360
  const visible = shownDistance > 10

  return (
    <div ref={containerRef} className="relative min-h-full">
      {/* Pull indicator */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center transition-all duration-200 z-50"
        style={{
          top: `${Math.max(shownDistance - 50, -50)}px`,
          opacity: visible ? 1 : 0,
          transform: `translateX(-50%) scale(${0.5 + progress * 0.5})`,
        }}
      >
        <div
          className={`
          flex items-center justify-center w-10 h-10 rounded-full
          bg-gradient-to-br from-slate-800 to-slate-900
          border border-slate-600/50 shadow-lg
          ${isRefreshing ? 'animate-pulse' : ''}
        `}
        >
          <RefreshCw
            size={20}
            className={`text-blue-400 transition-transform duration-200 ${isRefreshing ? 'animate-spin' : ''}`}
            style={{ transform: isRefreshing ? undefined : `rotate(${rotation}deg)` }}
          />
        </div>
      </div>

      {/* Hint text */}
      {visible && (
        <div
          role="status"
          className="absolute left-1/2 -translate-x-1/2 text-xs text-slate-400 font-medium transition-opacity z-50"
          style={{
            top: `${Math.max(shownDistance + 5, 5)}px`,
            opacity: isRefreshing ? 1 : progress,
          }}
        >
          {isRefreshing
            ? t('pullToRefresh.refreshing')
            : pullDistance >= PULL_THRESHOLD
              ? t('pullToRefresh.release')
              : t('pullToRefresh.pull')}
        </div>
      )}

      {/* Content with pull offset */}
      <div
        className="transition-transform duration-200"
        style={{
          transform: shownDistance > 0 ? `translateY(${shownDistance}px)` : 'none',
        }}
      >
        {children}
      </div>
    </div>
  )
}
